import { useGoogleMapsKey } from '../../hooks/useGoogleMapsKey';

export interface LocalizacaoMapaViewProps {
  urlLocalizacao?: string | null;
  /**
   * Exibe a explicação quando a URL não é embutível (link encurtado).
   *
   * `false` por padrão — nas telas de VISUALIZAÇÃO (torneio, partida) quem lê
   * não tem o que fazer com a informação: para ele basta o botão "Ver no
   * mapa", que leva ao lugar certo. A explicação só é útil na EDIÇÃO, onde o
   * organizador pode trocar pela URL completa; por isso o
   * `LocalizacaoMapaEditor` liga esta flag.
   */
  explicarQuandoNaoEmbutivel?: boolean;
}

/**
 * Extrai de uma URL do Google Maps o "query" que identifica o lugar — um
 * endereço, o nome do lugar ou um par `lat,lng`.
 *
 * Por que extrair em vez de só anexar `output=embed` na URL colada: o embed
 * do Google renderiza a partir de uma CONSULTA (`?q=<lugar>&output=embed`),
 * não de uma URL de navegação qualquer. Anexar o parâmetro a uma URL
 * arbitrária produz um iframe que o Google recusa enquadrar — o sintoma é o
 * quadro cinza com ícone de documento quebrado (reportado em QA manual,
 * 2026-07-20, com um short link).
 *
 * Retorna `null` quando a URL não carrega essa informação. O caso principal é
 * o SHORT LINK (`maps.app.goo.gl/XXXX`), que é apenas um redirect opaco: só o
 * servidor do Google sabe o destino, e resolvê-lo no browser esbarra em CORS.
 * Sem key nem backend, NÃO existe embed possível para ele — e é mais honesto
 * não renderizar mapa nenhum do que renderizar um quebrado.
 */
export function extrairQueryEmbed(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  // 1) Parâmetro explícito: `?q=` (formato clássico) ou `?query=` (Maps URLs
  //    API — é o formato que o próprio LocalizacaoMapaEditor gera ao escolher
  //    um resultado do Places).
  const paramQuery =
    parsed.searchParams.get('q') ?? parsed.searchParams.get('query');
  if (paramQuery && paramQuery.trim()) {
    return paramQuery.trim();
  }

  // 2) `/maps/place/<NOME>/...` — o nome do lugar vem no path.
  const place = parsed.pathname.match(/\/maps\/place\/([^/]+)/);
  if (place && place[1]) {
    const nome = decodeURIComponent(place[1].replace(/\+/g, ' ')).trim();
    if (nome) {
      return nome;
    }
  }

  // 3) `@lat,lng` — coordenadas no path (URL de navegação do Maps web).
  const coordenadas = parsed.pathname.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (coordenadas) {
    return `${coordenadas[1]},${coordenadas[2]}`;
  }

  return null;
}

/**
 * Monta o `src` do iframe a partir do query extraído.
 *
 * - COM key: usa a Maps Embed API oficial (`/maps/embed/v1/place`), que é
 *   gratuita e ilimitada e aceita exatamente esse `q=` estruturado.
 * - SEM key: usa o embed clássico `maps.google.com/maps?q=...&output=embed`,
 *   que funciona sem chave.
 *
 * Retorna `null` quando não há query extraível — o chamador não deve renderizar
 * iframe nesse caso.
 */
export function buildEmbedSrc(url: string, apiKey: string | null): string | null {
  const query = extrairQueryEmbed(url);
  if (!query) {
    return null;
  }

  if (apiKey) {
    const params = new URLSearchParams({ key: apiKey, q: query });
    return `https://www.google.com/maps/embed/v1/place?${params.toString()}`;
  }

  const params = new URLSearchParams({ q: query, output: 'embed' });
  return `https://maps.google.com/maps?${params.toString()}`;
}

/**
 * Componente reutilizável de VISUALIZAÇÃO de mapa (camada `components/`, sem
 * fetch próprio — recebe `urlLocalizacao` via props). Degrada em silêncio
 * (não renderiza nada, não lança erro) quando `urlLocalizacao` é nulo/vazio
 * (AC-A1/PRD AC-7). Quando preenchido, renderiza o embed keyless + o link
 * "Ver no mapa" sempre visível (mirror de `LocalDetalhe.tsx:190-199`),
 * fundação pronta para as Phases 3/4 (AC-A5/PRD AC-4).
 */
export default function LocalizacaoMapaView({
  urlLocalizacao,
  explicarQuandoNaoEmbutivel = false,
}: LocalizacaoMapaViewProps) {
  const { apiKey } = useGoogleMapsKey();

  const url = urlLocalizacao?.trim();
  if (!url) {
    return null;
  }

  const embedSrc = buildEmbedSrc(url, apiKey);

  return (
    <div>
      {embedSrc ? (
        <iframe
          title="Localização no mapa"
          src={embedSrc}
          loading="lazy"
          style={{ border: 0, width: '100%', height: 240 }}
        />
      ) : (
        // Sem query extraível (tipicamente um link encurtado): nunca um iframe
        // quebrado. Na visualização, some silenciosamente — resta o botão
        // "Ver no mapa", que funciona. Na edição (flag ligada), explicamos o
        // porquê, já que ali o organizador consegue agir.
        explicarQuandoNaoEmbutivel && (
          <p className="font-xssss text-grey-600 mb-2">
            Não é possível pré-visualizar o mapa a partir de um link encurtado.
            Use &quot;Ver no mapa&quot; abaixo, ou cole o link completo do
            Google Maps (o que contém o endereço ou as coordenadas) para exibir
            o mapa aqui.
          </p>
        )
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-light rounded-xl font-xssss ms-auto px-3"
      >
        <i className="feather-map-pin me-1"></i> Ver no mapa
      </a>
    </div>
  );
}
